/**
 * Prompt Template Selector Component
 * A dropdown selector for system prompt templates with preview and reset functionality.
 * Used in Workflow Prompt node LLM mode.
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { systemApi } from '@/lib/api/system';
import { ChevronDown, RotateCcw, FileText } from 'lucide-react';
import type { SystemPromptTemplateSimple } from '@/types/system';

interface PromptTemplateSelectorProps {
  /** Current system prompt value */
  value: string;
  /** Called when template is selected or prompt is edited */
  onChange: (prompt: string) => void;
  /** Called when reset is clicked - clears custom prompt to use default */
  onReset?: () => void;
  /** Filter templates by category */
  category?: string;
  /** Language for prompt display ('zh' or 'en') */
  language?: 'zh' | 'en';
  /** Placeholder text for the dropdown */
  placeholder?: string;
  /** Whether to show the reset button */
  showReset?: boolean;
  /** Custom class name for the container */
  className?: string;
}

export const PromptTemplateSelector: React.FC<PromptTemplateSelectorProps> = ({
  value,
  onChange,
  onReset,
  category,  // Default: undefined = show all templates
  language = 'zh',
  placeholder = '选择模版...',
  showReset = true,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Fetch templates for the specified category
  const { data: templates = [] } = useQuery({
    queryKey: ['promptTemplates', 'simple', category],
    queryFn: async () => {
      const allTemplates = await systemApi.promptTemplates.list(category);
      return allTemplates;
    },
  });

  // Get the current selected template info
  const selectedTemplate = useMemo(() => {
    if (!selectedKey) return null;
    return templates.find((t) => t.key === selectedKey);
  }, [selectedKey, templates]);

  // Handle template selection
  const handleSelect = async (template: SystemPromptTemplateSimple) => {
    setSelectedKey(template.key);
    setIsOpen(false);

    try {
      // Fetch the full template to get the prompt
      const fullTemplate = await systemApi.promptTemplates.getByKey(template.key);
      const prompt = language === 'zh' ? fullTemplate.system_prompt_zh : fullTemplate.system_prompt_en;
      onChange(prompt);
    } catch (error) {
      console.error('Failed to fetch template:', error);
    }
  };

  // Handle reset
  const handleReset = () => {
    setSelectedKey(null);
    if (onReset) {
      onReset();
    }
  };

  // Get display name for a template
  const getDisplayName = (template: { name: string; name_zh: string }) => {
    return language === 'zh' ? template.name_zh : template.name;
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        {/* Dropdown */}
        <div className="relative flex-1">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between text-sm hover:border-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          >
            <span className="flex items-center gap-2 truncate">
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-700 truncate">
                {selectedTemplate ? getDisplayName(selectedTemplate) : placeholder}
              </span>
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown menu */}
          {isOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {templates.length > 0 ? (
                templates.map((template) => (
                  <button
                    key={template.key}
                    type="button"
                    onClick={() => handleSelect(template)}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                      selectedKey === template.key ? 'bg-primary-50 text-primary-700' : ''
                    }`}
                  >
                    <div className="font-medium text-sm">{getDisplayName(template)}</div>
                    <div className="text-xs text-gray-500 font-mono">{template.key}</div>
                    {template.description_zh && language === 'zh' && (
                      <div className="text-xs text-gray-400 mt-0.5">{template.description_zh}</div>
                    )}
                    {template.description && language === 'en' && (
                      <div className="text-xs text-gray-400 mt-0.5">{template.description}</div>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">暂无可用模版</div>
              )}
            </div>
          )}
        </div>

        {/* Reset button */}
        {showReset && (selectedKey || value) && (
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors border border-gray-200"
            title="重置为默认"
          >
            <RotateCcw className="w-3 h-3" />
            重置
          </button>
        )}
      </div>
    </div>
  );
};

export default PromptTemplateSelector;
